/*
 * <moooxy-eye> — the Moooxy eye mark as a live, transparent web element.
 *
 * Plays the same intro as the Tesseract animation (Tesseract Projects/Eye Logo Reveal):
 *   merged eye -> lids crack open, catch, open wide and settle -> blink -> look left/right
 *   -> blink. After the intro the center eye follows the visitor's mouse (or finger),
 * and blinks now and then so it stays alive.
 *
 * Usage: load this file with a script tag (or inline it), then place a moooxy-eye element:
 *   moooxy-eye variant="red"   -> red lines, hollow pupils
 *   moooxy-eye variant="white" -> white lines, red pupils
 * (No HTML tags in this comment: when the code is inlined in a page, a closing script tag
 * here would end the script early.)
 *
 * Attributes:
 *   variant      "red" (default) or "white"
 *   skip-intro   present = start straight in follow mode
 *   texture      present = film grain + soft blur, like the photoshoot
 * Events / methods:
 *   "intro-end" event fires once when the intro finishes (or is skipped)
 *   finishIntro() jumps straight to follow mode
 *   blink() blinks once (after the intro)
 * Colors are CSS custom properties on the element (--line, --pupil); changing them
 * fades smoothly, e.g. eye.style.setProperty("--line", "#aa0000").
 * The element is square and transparent; size it with CSS width.
 */
(() => {
  // ---- Geometry (same units as the Tesseract build: 1080 canvas, mark drawn at 85%) ----
  const TIP = 480;            // lid tips, tucked under the outer ring
  const LID_K = 260;          // lid control-point height when fully open
  const HALO_DY = 221;        // halo distance from center when open
  const SPLIT0 = 700;         // ms: lids start to part
  const LOWER_LAG = 70;       // ms: lower lid trails the upper lid
  const INTRO_END = 5000;     // ms: hand-over to mouse follow
  const BLINKS = [1850, 4300];
  const LOOK = [[2400, 0], [2700, -1], [3050, -1], [3500, 1], [3850, 1], [4150, 0]];
  const IRIS_TRAVEL = 73;     // local units (62 canvas px)
  const PUPIL_TRAVEL = 34;

  // Mouse-follow range (local units). Vertical travel is smaller: the lids clip the iris.
  const FOLLOW = { irisX: 80, irisY: 38, pupilX: 34, pupilY: 18, reach: 0.9 };
  const IDLE_BLINK = [2800, 6500]; // random gap between idle blinks, ms

  const COLORS = {
    red: { line: "#aa0000", pupil: "none" },
    white: { line: "#fafcfe", pupil: "#bb0000" },
  };

  // ---- Easing ----
  function bezier(x1, y1, x2, y2) {
    const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
    const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
    const sx = (t) => ((ax * t + bx) * t + cx) * t;
    const sy = (t) => ((ay * t + by) * t + cy) * t;
    const dx = (t) => (3 * ax * t + 2 * bx) * t + cx;
    return (x) => {
      let t = x;
      for (let i = 0; i < 8; i++) {
        const d = dx(t);
        if (Math.abs(d) < 1e-6) break;
        t -= (sx(t) - x) / d;
      }
      return sy(Math.min(Math.max(t, 0), 1));
    };
  }
  const EASE_OUT = bezier(0.16, 1, 0.3, 1);
  const EASE_IN = bezier(0.55, 0, 1, 0.45);
  const EASE_IN_OUT = bezier(0.65, 0, 0.35, 1);
  const clamp01 = (v) => Math.min(Math.max(v, 0), 1);

  // Real-eye opening: crack open, catch, then open wide with a small overshoot and settle.
  function openCurve(ms) {
    const u = ms - SPLIT0;
    if (u <= 0) return 0;
    if (u < 170) { const q = u / 170; return 0.2 * (1 - Math.pow(1 - q, 3)); }
    if (u < 290) return 0.2 + 0.03 * ((u - 170) / 120);
    if (u < 720) {
      const q = (u - 290) / 430, c = 1.3;
      return 0.23 + 0.77 * (1 + (c + 1) * Math.pow(q - 1, 3) + c * Math.pow(q - 1, 2));
    }
    return 1;
  }

  // Blink: 1 = open, ~0.05 = shut. Snap shut, brief hold, ease back open.
  function blinkScale(ms, start) {
    const u = ms - start;
    if (u < 0 || u > 360) return 1;
    const SHUT = 4 / 85;
    if (u < 110) return 1 + (SHUT - 1) * EASE_IN(u / 110);
    if (u < 160) return SHUT;
    return SHUT + (1 - SHUT) * EASE_OUT((u - 160) / 200);
  }

  function scriptedLook(ms) {
    if (ms <= LOOK[0][0]) return 0;
    for (let i = 1; i < LOOK.length; i++) {
      const [t1, v1] = LOOK[i], [t0, v0] = LOOK[i - 1];
      if (ms <= t1) return v0 + (v1 - v0) * EASE_IN_OUT((ms - t0) / (t1 - t0));
    }
    return LOOK[LOOK.length - 1][1];
  }

  const almondPath = (kT, kB) =>
    `M${-TIP},0 C-250,${-kT} 250,${-kT} ${TIP},0 C250,${kB} -250,${kB} ${-TIP},0 Z`;
  const beyondLidPath = (sign, k) =>
    `M-700,0 L${-TIP},0 C-250,${sign * k} 250,${sign * k} ${TIP},0 L700,0 ` +
    `L700,${sign * 1400} L-700,${sign * 1400} Z`;

  let uid = 0;

  class MoooxyEye extends HTMLElement {
    static get observedAttributes() { return ["variant", "texture"]; }

    connectedCallback() {
      if (this._root) return;
      const id = (this._id = `mx${++uid}`);
      this._root = this.attachShadow({ mode: "open" });
      this._root.innerHTML = `
        <style>
          :host { display: inline-block; width: 320px; aspect-ratio: 1; line-height: 0; }
          svg { width: 100%; height: 100%; overflow: visible; }
          .ln { fill: none; stroke: var(--line); stroke-linejoin: round; transition: stroke .9s ease, fill .9s ease; }
          .pp { fill: var(--pupil); }
        </style>
        <svg viewBox="-540 -540 1080 1080" role="img" aria-label="Moooxy eye logo">
          <defs>
            <clipPath id="${id}-eye"><path data-k="eye"/></clipPath>
            <clipPath id="${id}-top"><path data-k="top"/></clipPath>
            <clipPath id="${id}-bot"><path data-k="bot"/></clipPath>
            <!-- texture: soft focus, a gentle sideways blur, and flickering film grain -->
            <filter id="${id}-fx" x="-20%" y="-6%" width="140%" height="112%" color-interpolation-filters="sRGB">
              <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="soft"/>
              <feGaussianBlur in="SourceGraphic" stdDeviation="16 2" result="smear"/>
              <feMerge result="body"><feMergeNode in="smear"/><feMergeNode in="soft"/></feMerge>
              <feTurbulence data-k="grain" type="fractalNoise" baseFrequency="0.55" numOctaves="2" seed="9" result="grain"/>
              <feGaussianBlur in="grain" stdDeviation="0.8" result="grainSoft"/>
              <feColorMatrix in="grainSoft" type="matrix" values="0.9 0 0 0 0.45  0.9 0 0 0 0.45  0.9 0 0 0 0.45  0 0 0 0 1" result="grainGray"/>
              <feComposite in="body" in2="grainGray" operator="arithmetic" k1="1" k2="0" k3="0" k4="0" result="gritty"/>
              <feComponentTransfer in="gritty" result="bright">
                <feFuncR type="linear" slope="1.15"/><feFuncG type="linear" slope="1.15"/><feFuncB type="linear" slope="1.15"/>
              </feComponentTransfer>
              <feComposite in="bright" in2="body" operator="in"/>
            </filter>
          </defs>
          <g data-k="art" transform="scale(0.85)">
            <g clip-path="url(#${id}-top)"><g data-k="haloTop">
              <ellipse class="ln" rx="158" ry="158" stroke-width="38"/>
              <ellipse class="ln pp" rx="61" ry="74" stroke-width="30"/>
            </g></g>
            <g clip-path="url(#${id}-bot)"><g data-k="haloBot">
              <ellipse class="ln" rx="158" ry="158" stroke-width="38"/>
              <ellipse class="ln pp" rx="61" ry="74" stroke-width="30"/>
            </g></g>
            <g clip-path="url(#${id}-eye)"><g data-k="iris">
              <ellipse class="ln" rx="124" ry="124" stroke-width="29"/>
              <ellipse data-k="pupil" class="ln pp" rx="41" ry="51" stroke-width="21"/>
            </g></g>
            <path data-k="outline" class="ln" stroke-width="42"/>
            <circle class="ln" r="502" stroke-width="62"/>
          </g>
        </svg>`;
      this._el = {};
      this._root.querySelectorAll("[data-k]").forEach((n) => (this._el[n.dataset.k] = n));
      this._applyVariant();
      this._applyTexture();
      this._grainAt = 0;

      this._pointer = null;
      this._follow = { ix: 0, iy: 0, px: 0, py: 0 };
      this._onMove = (e) => (this._pointer = { x: e.clientX, y: e.clientY });
      window.addEventListener("pointermove", this._onMove, { passive: true });
      window.addEventListener("pointerdown", this._onMove, { passive: true });

      const reduced = window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches;
      this._start = null;
      this._skip = this.hasAttribute("skip-intro") || reduced;
      this._nextBlink = null;
      this._idleBlinkAt = -1;
      this._raf = requestAnimationFrame((t) => this._frame(t));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this._raf);
      window.removeEventListener("pointermove", this._onMove);
      window.removeEventListener("pointerdown", this._onMove);
      this._root = null;
    }

    attributeChangedCallback() { if (this._root) { this._applyVariant(); this._applyTexture(); } }

    finishIntro() { this._skip = true; }

    blink() { this._blinkNow = true; }

    _applyTexture() {
      const on = this.hasAttribute("texture");
      if (on) this._el.art.setAttribute("filter", `url(#${this._id}-fx)`);
      else this._el.art.removeAttribute("filter");
    }

    _applyVariant() {
      const c = COLORS[this.getAttribute("variant")] || COLORS.red;
      this.style.setProperty("--line", c.line);
      this.style.setProperty("--pupil", c.pupil);
    }

    _frame(now) {
      this._raf = requestAnimationFrame((t) => this._frame(t));
      if (this._start === null) this._start = now;
      const ms = this._skip ? INTRO_END + 1000 : now - this._start;
      // texture: re-roll the grain like film (~12 fps)
      if (this._el.art.hasAttribute("filter") && now - this._grainAt > 83) {
        this._grainAt = now;
        this._el.grain.setAttribute("seed", (Math.random() * 1000) | 0);
      }
      const el = this._el;
      if (ms >= INTRO_END && !this._introDone) {
        this._introDone = true;
        this.dispatchEvent(new Event("intro-end", { bubbles: true }));
      }

      // Lids, eye mask, and the halo halves they carry
      const eT = openCurve(ms), eB = openCurve(ms - LOWER_LAG);
      const kT = LID_K * eT, kB = LID_K * eB;
      const almond = almondPath(kT, kB);
      el.outline.setAttribute("d", almond);
      el.eye.setAttribute("d", almond);
      el.top.setAttribute("d", beyondLidPath(-1, kT));
      el.bot.setAttribute("d", beyondLidPath(1, kB));
      el.outline.style.opacity = ms <= SPLIT0 ? 0 : EASE_OUT(clamp01((ms - SPLIT0) / 120));
      el.haloTop.setAttribute("transform", `translate(0 ${-HALO_DY * eT})`);
      el.haloBot.setAttribute("transform", `translate(0 ${HALO_DY * eB})`);
      el.iris.style.opacity = ms < SPLIT0 + 20 ? 0 : 1;

      // Gaze and blinks
      let ix, iy = 0, px, py = 0, blink;
      if (ms < INTRO_END) {
        const d = scriptedLook(ms);
        ix = d * IRIS_TRAVEL; px = d * PUPIL_TRAVEL;
        blink = Math.min(...BLINKS.map((b) => blinkScale(ms, b)));
      } else {
        const target = this._gazeTarget();
        // ease toward the target; ramps in from the intro's resting center
        const f = this._follow, a = 0.14;
        f.ix += (target.x * FOLLOW.irisX - f.ix) * a;
        f.iy += (target.y * FOLLOW.irisY - f.iy) * a;
        f.px += (target.x * FOLLOW.pupilX - f.px) * a;
        f.py += (target.y * FOLLOW.pupilY - f.py) * a;
        ix = f.ix; iy = f.iy; px = f.px; py = f.py;
        if (this._idleBlinkAt < 0 || ms > this._idleBlinkAt + 400) {
          this._idleBlinkAt = ms + IDLE_BLINK[0] + Math.random() * (IDLE_BLINK[1] - IDLE_BLINK[0]);
        }
        if (this._blinkNow) { this._blinkNow = false; this._idleBlinkAt = ms; }
        blink = blinkScale(ms, this._idleBlinkAt);
      }
      el.iris.setAttribute("transform", `translate(${ix} ${iy}) scale(1 ${blink})`);
      el.pupil.setAttribute("transform", `translate(${px} ${py})`);
    }

    // Direction from the eye to the pointer, -1..1 on each axis, eased by distance.
    _gazeTarget() {
      if (!this._pointer) return { x: 0, y: 0 };
      const r = this.getBoundingClientRect();
      const dx = this._pointer.x - (r.left + r.width / 2);
      const dy = this._pointer.y - (r.top + r.height / 2);
      const dist = Math.hypot(dx, dy);
      if (dist < 1) return { x: 0, y: 0 };
      // full deflection once the pointer is about one eye-width away
      const reach = Math.min(dist / (r.width * FOLLOW.reach), 1);
      const s = Math.sin((reach * Math.PI) / 2);
      return { x: (dx / dist) * s, y: (dy / dist) * s };
    }
  }

  if (!customElements.get("moooxy-eye")) customElements.define("moooxy-eye", MoooxyEye);
})();
